import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { useT } from "@/contexts/I18nContext";

/**
 * A dialog.
 *
 * The native `<dialog>` element, so the browser handles the focus trap, the
 * backdrop, Escape, and inertness of the page behind it. Reimplementing those
 * by hand is how a modal ends up unusable with a keyboard.
 */
export function Modal({
  open,
  onClose,
  titleKey,
  titleParams,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  titleKey: string;
  titleParams?: Record<string, unknown>;
  children: ReactNode;
  footer?: ReactNode;
}) {
  const t = useT();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    // Escape fires `cancel`, and the default would close the element without
    // telling React, leaving `open` true and the dialog unopenable afterwards.
    const onCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener("cancel", onCancel);
    return () => dialog.removeEventListener("cancel", onCancel);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      aria-labelledby="modal-title"
      /* A dialog floats over the page, so it takes the shadow, not a hairline. */
      className="w-full max-w-lg rounded-card bg-white p-0 text-ink shadow-pop backdrop:bg-ink/40"
    >
      <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
        <h2 id="modal-title" className="section-title">
          {t(titleKey, titleParams)}
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="-mr-1 -mt-1 rounded-control p-1.5 text-ink-muted hover:bg-surface-muted hover:text-ink"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>
      <div className="px-5 py-5">{children}</div>
      {footer ? (
        <div className="flex justify-end gap-2 border-t border-line px-5 py-4">{footer}</div>
      ) : null}
    </dialog>
  );
}
